import React, { useRef } from "react";

import { observer } from "mobx-react-lite";
import { useDrag, useDrop } from "react-dnd";
import { makeStyles } from "tss-react/mui";

import { FormElementKind } from "@/gql/graphql";
import useDebounce from "@/hooks/useDebounce";
import { colors } from "@/theme/overrides/colors";

import FormAffirmation from "./RightPanel/FormAffirmation";
import FormDate from "./RightPanel/FormDate";
import FormDescription from "./RightPanel/FormDescription";
import FormDropdown from "./RightPanel/FormDropdown";
import FormFile from "./RightPanel/FormFile";
import FormHeader from "./RightPanel/FormHeader";
import FormLongText from "./RightPanel/FormLongText";
import FormMedia from "./RightPanel/FormMedia";
import FormMediaUrl from "./RightPanel/FormMediaUrl";
import FormNumber from "./RightPanel/FormNumber";
import FormShortText from "./RightPanel/FormShortText";
import FormSignature from "./RightPanel/FormSignature";
import FormVerticalMultipleSelector from "./RightPanel/FormVerticalMultipleSelector";
import FormVerticalSingleSelector from "./RightPanel/FormVerticalSingleSelector";
import { DragType } from "./utils/types";
import FormElement from "../store/FormElement";

interface Props {
  element: FormElement;
}

export const useStyles = makeStyles()({
  root: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    padding: 8,
    marginBottom: 8,
    border: "2px dotted transparent",
    "&:hover": {
      borderColor: colors.grey,
    },
    "&:hover .edit-icons-container": {
      visibility: "visible",
    },
    "&:hover .styled-drag-indicator": {
      visibility: "visible",
    },
  },
  dragging: {
    opacity: 0,
  },
});

const FormElementView: React.FC<Props> = observer(({ element }) => {
  const { classes, cx } = useStyles();
  const ref = useRef<HTMLDivElement>(null);

  const swapWith = useDebounce(
    (item: FormElement) => {
      element.section.swapElements(element, item as FormElement);
    },
    100,
    { leading: true, trailing: false }
  );

  const [{ isDragging }, drag] = useDrag({
    type: DragType.FORMCOMPONENT,
    item: () => {
      return element;
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [{ handlerId }, drop] = useDrop({
    accept: DragType.FORMCOMPONENT,

    collect: (monitor) => ({
      handlerId: monitor.getHandlerId(),
    }),

    hover(item) {
      // Don't replace items with themselves
      if (element === item) return;

      // Only performing the move when the mouse has crossed half of the items
      // height can cause issues when items aren't the same height, so we're
      // no longer doing it.
      // To avoid jank when swapping two items where one item is much smaller and
      // can contain inside another item (causes them to be swapped back and forth
      // very rapidly), we debounce the swap so we don't swap very rapidly.
      swapWith(item as FormElement);
    },
  });

  drag(drop(ref));

  function renderElement() {
    switch (element.kind) {
      case FormElementKind.Header:
        return <FormHeader element={element} />;
      case FormElementKind.Description:
        return <FormDescription element={element} />;
      case FormElementKind.LongText:
        return <FormLongText element={element} />;
      case FormElementKind.ShortText:
        return <FormShortText element={element} />;
      case FormElementKind.Dropdown:
        return <FormDropdown element={element} />;
      case FormElementKind.Number:
        return <FormNumber element={element} />;
      case FormElementKind.VerticalSingleSelector:
        return <FormVerticalSingleSelector element={element} />;
      case FormElementKind.VerticalMultipleSelector:
        return <FormVerticalMultipleSelector element={element} />;
      case FormElementKind.Date:
        return <FormDate element={element} />;
      case FormElementKind.Media:
        return <FormMedia element={element} />;
      case FormElementKind.MediaUrl:
        return <FormMediaUrl element={element} />;
      case FormElementKind.File:
        return <FormFile element={element} />;
      case FormElementKind.Checkbox:
        return <FormAffirmation element={element} />;
      case FormElementKind.Signature:
        return <FormSignature element={element} />;
      default:
        return null;
    }
  }

  return (
    <div
      className={cx(classes.root, isDragging && classes.dragging)}
      data-handler-id={handlerId}
      ref={ref}
    >
      {renderElement()}
    </div>
  );
});

export default FormElementView;
